import { Component } from '@theme/component';

/**
 * Scroll-pinned story section with crossfaded images, eased scrubbing, and synced text/progress.
 * Mobile (≤749px): swipe carousel with progress under each image — no sticky scroll scrub.
 *
 * @typedef {Object} SknlyScrollStoryRefs
 * @property {HTMLElement} [mediaStack]
 * @property {HTMLElement[]} [textPanels]
 * @property {HTMLElement[]} [progressSegments]
 * @property {HTMLElement[]} [mediaSlides]
 */

/** @param {number} value @param {number} min @param {number} max */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/** Smooth luxury-style easing for crossfades. @param {number} t */
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * @extends {Component<SknlyScrollStoryRefs>}
 */
class SknlyScrollStory extends Component {
  /** @type {boolean} */
  #reducedMotion = false;

  /** @type {boolean} */
  #isVisible = true;

  /** @type {boolean} */
  #scrollPending = false;

  /** @type {boolean} */
  #scrubEnabled = false;

  /** @type {boolean} */
  #carouselEnabled = false;

  /** @type {number} */
  #lastVisualStep = -1;

  /** @type {number} */
  #carouselIndex = 0;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {MediaQueryList | undefined} */
  #mobileMq;

  /** @type {HTMLElement | null} */
  #carouselScroller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#mobileMq = window.matchMedia('(max-width: 749px)');
    this.#mobileMq.addEventListener('change', this.#onViewportChange);

    for (const panel of this.querySelectorAll('[data-text-panel]')) {
      panel.removeAttribute('hidden');
    }

    this.#setupForViewport();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardownScrub();
    this.#teardownCarousel();
    this.#mobileMq?.removeEventListener('change', this.#onViewportChange);
  }

  #onViewportChange = () => {
    this.#setupForViewport();
  };

  #isMobileViewport() {
    return this.#mobileMq?.matches ?? window.matchMedia('(max-width: 749px)').matches;
  }

  #setupForViewport() {
    this.#teardownScrub();
    this.#teardownCarousel();

    if (this.#isMobileViewport()) {
      this.#scrubEnabled = false;
      this.dataset.mode = 'mobile-carousel';
      this.#bindCarousel();
      return;
    }

    this.dataset.mode = 'scroll-pin';

    if (this.#reducedMotion) {
      this.#scrubEnabled = false;
      this.#applyFrame(0);
      this.#syncProgressHeight();
      return;
    }

    this.#scrubEnabled = true;
    this.#bindScrub();
  }

  #bindScrub() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.#isVisible = entries.some((entry) => entry.isIntersecting);

        if (this.#isVisible) {
          this.#scheduleUpdate();
        }
      },
      { rootMargin: '20% 0px' }
    );
    this.#intersectionObserver.observe(this);

    const viewport = this.querySelector('.sknly-scroll-story__media-viewport');
    if (viewport instanceof HTMLElement) {
      this.#resizeObserver = new ResizeObserver(() => this.#syncProgressHeight());
      this.#resizeObserver.observe(viewport);
    }

    this.#onScroll = () => this.#scheduleUpdate();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#syncProgressHeight();
    this.#scheduleUpdate();
  }

  #teardownScrub() {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    this.#scrubEnabled = false;
    this.#scrollPending = false;
    this.#lastVisualStep = -1;
  }

  #bindCarousel() {
    const scroller = this.querySelector('[data-carousel-scroller]');
    if (!(scroller instanceof HTMLElement)) return;

    this.#carouselScroller = scroller;
    this.#carouselEnabled = true;
    this.#carouselIndex = 0;

    scroller.addEventListener('scroll', this.#onCarouselScroll, { passive: true });
    scroller.addEventListener('keydown', this.#onCarouselKeydown);

    const progress = this.querySelector('[data-mobile-progress]');
    progress?.addEventListener('click', this.#onProgressClick);

    this.#applyCarouselStep(0, true);
  }

  #teardownCarousel() {
    if (this.#carouselScroller) {
      this.#carouselScroller.removeEventListener('scroll', this.#onCarouselScroll);
      this.#carouselScroller.removeEventListener('keydown', this.#onCarouselKeydown);
    }

    const progress = this.querySelector('[data-mobile-progress]');
    progress?.removeEventListener('click', this.#onProgressClick);

    this.#carouselScroller = null;
    this.#carouselEnabled = false;
    this.#carouselIndex = 0;
  }

  /**
   * @param {MouseEvent} event
   */
  #onProgressClick = (event) => {
    if (!this.#carouselEnabled) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const segment = target.closest('[data-mobile-progress-segment]');
    if (!(segment instanceof HTMLElement)) return;

    const index = Number(segment.dataset.stepIndex);
    if (Number.isNaN(index)) return;

    this.#goToCarouselSlide(index);
  };

  #onCarouselScroll = () => {
    if (!this.#carouselEnabled || !this.#carouselScroller) return;

    if (this.#scrollPending) return;

    this.#scrollPending = true;
    requestAnimationFrame(() => {
      this.#scrollPending = false;
      this.#syncCarouselFromScroll();
    });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onCarouselKeydown = (event) => {
    if (!this.#carouselEnabled || !this.#carouselScroller) return;

    const stepCount = this.#getStepCount();
    if (stepCount <= 1) return;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.#goToCarouselSlide(Math.min(this.#carouselIndex + 1, stepCount - 1));
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.#goToCarouselSlide(Math.max(this.#carouselIndex - 1, 0));
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.#goToCarouselSlide(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.#goToCarouselSlide(stepCount - 1);
    }
  };

  #syncCarouselFromScroll() {
    const scroller = this.#carouselScroller;
    if (!(scroller instanceof HTMLElement)) return;

    const slideWidth = scroller.clientWidth;
    if (slideWidth <= 0) return;

    const rawStep = scroller.scrollLeft / slideWidth;
    const step = clamp(Math.round(rawStep), 0, this.#getStepCount() - 1);

    this.#applyCarouselStep(step, false);
    this.#applyCarouselProgress(rawStep);
  }

  /**
   * @param {number} index
   */
  #goToCarouselSlide(index) {
    const scroller = this.#carouselScroller;
    if (!(scroller instanceof HTMLElement)) return;

    const slideWidth = scroller.clientWidth;
    scroller.scrollTo({
      left: index * slideWidth,
      behavior: this.#reducedMotion ? 'auto' : 'smooth',
    });

    this.#applyCarouselStep(index, true);
  }

  /**
   * @param {number} step
   * @param {boolean} syncProgress
   */
  #applyCarouselStep(step, syncProgress) {
    const slides = this.querySelectorAll('[data-carousel-slide]');

    for (const [index, slide] of slides.entries()) {
      if (!(slide instanceof HTMLElement)) continue;

      const isActive = index === step;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    }

    const segments = this.querySelectorAll('[data-mobile-progress-segment]');
    for (const [index, segment] of segments.entries()) {
      if (!(segment instanceof HTMLElement)) continue;

      const isActive = index === step;
      segment.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }

    this.#carouselIndex = step;
    this.dataset.activeStep = String(step);

    if (syncProgress) {
      this.#applyCarouselProgress(step);
    }
  }

  /**
   * Fill mobile progress segments (duplicated under each slide’s image).
   * @param {number} rawStep
   */
  #applyCarouselProgress(rawStep) {
    const stepCount = this.#getStepCount();
    const clampedRaw = clamp(rawStep, 0, Math.max(stepCount - 1, 0));
    const step = Math.min(stepCount - 1, Math.max(0, Math.floor(clampedRaw + 0.001)));

    const progresses = this.querySelectorAll('[data-mobile-progress]');

    for (const progress of progresses) {
      if (!(progress instanceof HTMLElement)) continue;

      const segments = progress.querySelectorAll('[data-mobile-progress-segment]');

      for (const [index, segment] of segments.entries()) {
        if (!(segment instanceof HTMLElement)) continue;

        const fill = clamp(clampedRaw - index + 1, 0, 1);

        segment.style.setProperty('--segment-fill', String(fill));
        segment.classList.toggle('is-active', index === step && fill < 1);
        segment.classList.toggle('is-complete', fill >= 1);
        segment.classList.toggle('is-filled', fill >= 1);
      }
    }
  }

  #onScroll = () => {};

  #scheduleUpdate() {
    if (!this.#scrubEnabled || this.#scrollPending || !this.#isVisible) return;

    this.#scrollPending = true;
    requestAnimationFrame(() => {
      this.#scrollPending = false;
      this.#onScrollImpl();
    });
  }

  #getStepCount() {
    return Number(this.dataset.stepCount || '1');
  }

  #onScrollImpl() {
    const stepCount = this.#getStepCount();

    if (stepCount <= 1) {
      this.#applyFrame(0);
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const scrollable = this.offsetHeight - viewportHeight;

    if (scrollable <= 0) {
      this.#applyFrame(0);
      return;
    }

    const scrolled = clamp(-rect.top, 0, scrollable);
    const progress = scrolled / scrollable;
    const rawStep = progress * (stepCount - 1);

    this.#applyFrame(rawStep);
  }

  /**
   * @param {number} rawStep
   */
  #applyFrame(rawStep) {
    const stepCount = this.#getStepCount();
    const clampedRaw = clamp(rawStep, 0, Math.max(stepCount - 1, 0));
    const step = Math.min(stepCount - 1, Math.max(0, Math.floor(clampedRaw)));
    const visualStep = Math.round(clampedRaw * 1000) / 1000;
    const shouldUpdateVisuals = visualStep !== this.#lastVisualStep;

    if (shouldUpdateVisuals) {
      this.#lastVisualStep = visualStep;

      const slides = this.querySelectorAll('[data-scroll-step]');
      for (const [index, slide] of slides.entries()) {
        if (!(slide instanceof HTMLElement)) continue;

        const distance = Math.abs(clampedRaw - index);
        const blend = distance >= 1 ? 0 : 1 - easeInOutCubic(Math.min(distance, 1));
        const scale = 1 + Math.min(distance, 1) * 0.045;

        slide.style.opacity = String(blend);
        slide.style.transform = `scale(${scale})`;
        slide.style.zIndex = String(Math.round((1 - Math.min(distance, 1)) * 100));
        slide.style.visibility = blend < 0.02 ? 'hidden' : 'visible';
        slide.classList.toggle('is-active', index === step);
      }

      const panels = this.querySelectorAll('[data-text-panel]');
      for (const [index, panel] of panels.entries()) {
        if (!(panel instanceof HTMLElement)) continue;

        const distance = Math.abs(clampedRaw - index);
        const blend = distance >= 1 ? 0 : 1 - easeInOutCubic(Math.min(distance, 1));
        const translateY = Math.min(distance, 1) * 14;

        panel.style.setProperty('--panel-opacity', String(blend));
        panel.style.setProperty('--panel-y', `${translateY}px`);
        panel.style.visibility = blend < 0.02 ? 'hidden' : 'visible';
        panel.classList.toggle('is-active', index === step);
        panel.setAttribute('aria-hidden', blend < 0.5 ? 'true' : 'false');
      }
    }

    const segments = this.querySelectorAll('[data-progress-segment]');
    for (const [index, segment] of segments.entries()) {
      if (!(segment instanceof HTMLElement)) continue;

      // Segment i fills as we approach image i+1: fill = clamp(rawStep - i + 1, 0, 1).
      // Segment 0 stays full once we're in the story (never empties on image 1).
      // Scroll-driven: grows top→bottom going down; shrinks bottom→top going up.
      const fill = clamp(clampedRaw - index + 1, 0, 1);

      segment.style.setProperty('--segment-fill', String(fill));
      segment.classList.toggle('is-active', index === step && fill < 1);
      segment.classList.toggle('is-complete', fill >= 1);
      segment.classList.toggle('is-filled', fill >= 1);
    }

    this.#syncProgressHeight();

    this.dataset.activeStep = String(step);
  }

  #syncProgressHeight() {
    const viewport = this.querySelector('.sknly-scroll-story__media-viewport');
    const progress = this.querySelector('.sknly-scroll-story__progress:not(.sknly-scroll-story__progress--carousel)');

    if (!(viewport instanceof HTMLElement) || !(progress instanceof HTMLElement)) return;

    /* Horizontal progress sizing is mobile-carousel only (≤749). Desktop/tablet stay vertical. */
    const isMobileCarousel = window.matchMedia('(max-width: 749px)').matches;

    if (isMobileCarousel) {
      progress.style.width = `${viewport.offsetWidth}px`;
      progress.style.height = '2px';
      return;
    }

    const height = Math.round(viewport.getBoundingClientRect().height || viewport.offsetHeight);

    progress.style.width = '2px';
    progress.style.flex = '0 0 2px';

    if (height > 0) {
      progress.style.height = `${height}px`;
      progress.style.minHeight = `${height}px`;
    }
  }
}

if (!customElements.get('sknly-scroll-story')) {
  customElements.define('sknly-scroll-story', SknlyScrollStory);
}
